import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
} from '@tanstack/react-query';
import { callApiPromise } from '@/runtimes/get-runtime';
import { adminUsersQueryKey } from '@/components/web-fe/admin/query-keys';
import { authClient } from '@/lib/auth-client';
import { useImpersonate } from '@/components/web-fe/admin/use-impersonate';
import {
  BaError,
  unwrapBaResponse,
} from '@/components/web-fe/admin/unwrap-ba-response';
import type { SubscriptionHistoryRow } from '@listening/api-contract';
import { Button } from '@listening/frontend/components/radix/button';
import { Input } from '@listening/frontend/components/radix/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: 'users.$userId' });

const userDetailQuery = (userId: string) =>
  queryOptions({
    queryKey: ['admin', 'user-detail', userId],
    queryFn: () =>
      callApiPromise((api) => api.Admin.GetUserDetail({ params: { userId } })),
  });

const subscriptionHistoryQueryFor = (userId: string) =>
  queryOptions({
    queryKey: ['admin', 'subscription-history', userId],
    queryFn: () =>
      callApiPromise((api) =>
        api.Admin.GetUserSubscriptionHistory({
          params: { userId },
          query: { pageIndex: 0, pageSize: 50 },
        }),
      ),
  });

export const Route = createFileRoute('/app/_authenticated/admin/users/$userId')(
  {
    component: UserDetailPage,
  },
);

function UserDetailPage() {
  const { userId } = Route.useParams();
  const queryClient = useQueryClient();

  const detailQuery = useQuery(userDetailQuery(userId));
  const subscriptionHistoryQuery = useQuery(
    subscriptionHistoryQueryFor(userId),
  );

  const syncBillingMutation = useMutation({
    mutationFn: () =>
      callApiPromise((api) =>
        api.Admin.SyncUserBilling({ params: { userId } }),
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: userDetailQuery(userId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: subscriptionHistoryQueryFor(userId).queryKey,
      });
      if (result.errors.length > 0) {
        toast.warning(
          `Sync completed with errors: ${result.errors.join(', ')}`,
        );
      } else {
        toast.success('Billing data refreshed');
      }
    },
    onError: (error) => {
      moduleLogger.error({ err: error }, 'Failed to sync billing');
      toast.error('Failed to sync billing');
    },
  });

  const [passwordDialog, setPasswordDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const impersonateMutation = useImpersonate();

  if (detailQuery.isLoading) {
    return (
      <div className='py-12 text-center text-gray-500 dark:text-steel-180'>
        Loading user details...
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className='py-12 text-center text-red-500'>
        Failed to load user details.
      </div>
    );
  }

  const user = detailQuery.data;

  return (
    <div>
      <Link
        to='/app/admin'
        className='mb-4 inline-flex text-sm text-gray-500 hover:text-gray-700 dark:text-steel-180 dark:hover:text-white'
      >
        &larr; Back to users
      </Link>

      <div className='mb-6 flex items-start justify-between'>
        <div>
          <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
            {user.name || 'No name'}
          </h2>
          <p className='text-gray-500 dark:text-steel-180'>{user.email}</p>
        </div>
        <div className='flex gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPasswordDialog(true)}
          >
            Set Password
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setEmailDialog(true)}
          >
            Set Email
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setImpersonateDialog(true)}
          >
            Impersonate
          </Button>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => setDeleteDialog(true)}
          >
            Delete User
          </Button>
        </div>
      </div>

      {/* Account Info */}
      <Section title='Account Info'>
        <Field label='ID' value={user.id} />
        <Field label='Email' value={user.email} />
        <Field
          label='Email Verified'
          value={user.emailVerified ? 'Yes' : 'No'}
        />
        <Field label='Role' value={user.role ?? 'user'} />
        <Field
          label='Status'
          value={
            user.banned
              ? `Banned${user.banReason ? ` (${user.banReason})` : ''}`
              : 'Active'
          }
        />
        {user.banExpires && (
          <Field label='Ban Expires' value={formatDate(user.banExpires)} />
        )}
        <Field label='Created' value={formatDate(user.createdAt)} />
        <Field label='Updated' value={formatDate(user.updatedAt)} />
        <Field label='Referral Code' value={user.referralCode ?? 'None'} />
        {user.deletedAt && (
          <Field label='Deleted At' value={formatDate(user.deletedAt)} />
        )}
      </Section>

      {/* Subscription / Billing */}
      <Section
        title='Billing'
        action={
          <Button
            variant='outline'
            size='sm'
            onClick={() => syncBillingMutation.mutate()}
            disabled={syncBillingMutation.isPending}
          >
            {syncBillingMutation.isPending ? 'Syncing...' : 'Sync Billing'}
          </Button>
        }
      >
        {user.billing ? (
          <>
            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              Stripe
            </h4>
            <Field
              label='Customer ID'
              value={user.billing.stripeCustomerId ?? 'None'}
            />
            <Field
              label='Subscription ID'
              value={user.billing.stripeSubscriptionId ?? 'None'}
            />
            <Field
              label='Status'
              value={user.billing.stripeSubscriptionStatus ?? 'None'}
            />
            <Field
              label='Current Period End'
              value={formatDate(user.billing.stripeCurrentPeriodEnd)}
            />
            <Field
              label='Cancel at Period End'
              value={user.billing.stripeCancelAtPeriodEnd ? 'Yes' : 'No'}
            />
            <Field
              label='Trial End'
              value={formatDate(user.billing.stripeTrialEnd)}
            />
            <Field
              label='Last Synced'
              value={formatDate(user.billing.lastSyncedStripeAt)}
            />

            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              RevenueCat
            </h4>
            <Field
              label='Status'
              value={user.billing.rcSubscriptionStatus ?? 'None'}
            />
            <Field
              label='Current Period End'
              value={formatDate(user.billing.rcCurrentPeriodEnd)}
            />
            <Field
              label='Trial End'
              value={formatDate(user.billing.rcTrialEnd)}
            />
            <Field
              label='Last Synced'
              value={formatDate(user.billing.lastSyncedRevenueCatAt)}
            />

            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              Referral
            </h4>
            <Field
              label='Start'
              value={formatDate(user.billing.referralStartAt)}
            />
            <Field label='End' value={formatDate(user.billing.referralEndAt)} />
            <Field
              label='Referred By'
              value={user.billing.referredByUserId ?? 'None'}
            />

            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              General
            </h4>
            <Field
              label='Has Ever Paid'
              value={user.billing.hasEverPaid ? 'Yes' : 'No'}
            />
          </>
        ) : (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No billing record
          </p>
        )}
      </Section>

      {/* Subscription History */}
      <Section
        title='Subscription History'
        action={
          subscriptionHistoryQuery.data ? (
            <span className='text-xs text-gray-500 dark:text-steel-180'>
              {subscriptionHistoryQuery.data.total} event
              {subscriptionHistoryQuery.data.total === 1 ? '' : 's'}
            </span>
          ) : null
        }
      >
        {subscriptionHistoryQuery.isLoading ? (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            Loading...
          </p>
        ) : subscriptionHistoryQuery.isError ? (
          <p className='text-sm text-red-500'>Failed to load</p>
        ) : subscriptionHistoryQuery.data?.events.length === 0 ? (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No events yet
          </p>
        ) : (
          <SubscriptionHistoryTable
            events={subscriptionHistoryQuery.data?.events ?? []}
          />
        )}
      </Section>

      {/* Preferences */}
      <Section title='Preferences'>
        {user.preferences ? (
          <Field label='TTS Voice' value={user.preferences.voice} />
        ) : (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No preferences set
          </p>
        )}
      </Section>

      {/* Marketing Survey */}
      <Section title='Marketing Survey'>
        {user.marketingSurvey ? (
          <>
            <Field
              label='Acquisition Source'
              value={user.marketingSurvey.acquisitionSource}
            />
            <Field
              label='Details'
              value={user.marketingSurvey.details ?? 'None'}
            />
          </>
        ) : (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No survey submitted
          </p>
        )}
      </Section>

      {/* Impersonate Dialog */}
      <Dialog open={impersonateDialog} onOpenChange={setImpersonateDialog}>
        <DialogContent includeCloseButton>
          <DialogHeader>
            <DialogTitle>Confirm Impersonation</DialogTitle>
            <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
              You are about to impersonate <strong>{user.email}</strong>.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setImpersonateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => impersonateMutation.mutate(user.id)}
              disabled={impersonateMutation.isPending}
            >
              {impersonateMutation.isPending
                ? 'Impersonating...'
                : 'Impersonate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        {passwordDialog && (
          <ChangePasswordDialog
            userId={user.id}
            email={user.email}
            onClose={() => setPasswordDialog(false)}
          />
        )}
      </Dialog>

      {/* Set Email Dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        {emailDialog && (
          <ChangeEmailDialog
            userId={user.id}
            currentEmail={user.email}
            onClose={() => {
              setEmailDialog(false);
              void queryClient.invalidateQueries({
                queryKey: userDetailQuery(userId).queryKey,
              });
            }}
          />
        )}
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        {deleteDialog && (
          <DeleteUserDialog
            userId={user.id}
            email={user.email}
            onClose={() => setDeleteDialog(false)}
          />
        )}
      </Dialog>
    </div>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className='mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-260 dark:bg-navy-760'>
      <div className='mb-3 flex items-center justify-between'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex border-b border-gray-100 py-1.5 last:border-0 dark:border-navy-260'>
      <span className='w-48 shrink-0 text-sm font-medium text-gray-500 dark:text-steel-180'>
        {label}
      </span>
      <span className='text-sm text-gray-900 dark:text-white'>{value}</span>
    </div>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString();
}

function SubscriptionHistoryTable({
  events,
}: {
  events: readonly SubscriptionHistoryRow[];
}) {
  return (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-gray-200 text-left text-xs text-gray-500 uppercase dark:border-navy-260 dark:text-steel-180'>
            <th className='py-2 pr-4'>When</th>
            <th className='py-2 pr-4'>Provider</th>
            <th className='py-2 pr-4'>Event</th>
            <th className='py-2 pr-4'>Source</th>
            <th className='py-2 pr-4'>Period End</th>
            <th className='py-2 pr-4'>Provider Event ID</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr
              key={e.id}
              className='border-b border-gray-100 align-top dark:border-navy-260'
            >
              <td className='py-2 pr-4 whitespace-nowrap text-gray-900 dark:text-white'>
                {formatDate(e.createdAt)}
              </td>
              <td className='py-2 pr-4 text-gray-700 dark:text-steel-180'>
                {e.provider}
              </td>
              <td className='py-2 pr-4 font-medium text-gray-900 dark:text-white'>
                {e.eventType}
              </td>
              <td className='py-2 pr-4 text-gray-700 dark:text-steel-180'>
                {e.eventSource}
              </td>
              <td className='py-2 pr-4 text-gray-700 dark:text-steel-180'>
                {formatDate(e.periodEndAt)}
              </td>
              <td className='py-2 pr-4 font-mono text-xs text-gray-500 dark:text-steel-180'>
                {e.providerEventId ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Dialogs
// =============================================================================

function ChangePasswordDialog({
  userId,
  email,
  onClose,
}: {
  userId: string;
  email: string;
  onClose: () => void;
}) {
  const [newPassword, setNewPassword] = useState('');

  const mutation = useMutation({
    mutationFn: async (password: string) => {
      const result = await authClient.admin.setUserPassword({
        userId,
        newPassword: password,
      });
      return unwrapBaResponse(result);
    },
    onSuccess: () => {
      toast.success('Password updated');
      onClose();
    },
    onError: (error) => {
      moduleLogger.error({ err: error }, 'Failed to update password');
      toast.error(
        error instanceof BaError ? error.message : 'Failed to update password',
      );
    },
  });

  return (
    <DialogContent includeCloseButton>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (newPassword.trim()) mutation.mutate(newPassword);
        }}
      >
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
            Set a new password for {email}
          </p>
        </DialogHeader>
        <div className='py-4'>
          <Input
            type='password'
            placeholder='New password'
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant='outline' type='button' onClick={onClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            disabled={mutation.isPending || !newPassword.trim()}
          >
            {mutation.isPending ? 'Saving...' : 'Set Password'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ChangeEmailDialog({
  userId,
  currentEmail,
  onClose,
}: {
  userId: string;
  currentEmail: string;
  onClose: () => void;
}) {
  const [newEmail, setNewEmail] = useState(currentEmail);

  const mutation = useMutation({
    mutationFn: async (email: string) => {
      const result = await authClient.admin.updateUser({
        userId,
        data: { email },
      });
      return unwrapBaResponse(result);
    },
    onSuccess: () => {
      toast.success('Email updated');
      onClose();
    },
    onError: (error) => {
      moduleLogger.error({ err: error }, 'Failed to update email');
      toast.error(
        error instanceof BaError ? error.message : 'Failed to update email',
      );
    },
  });

  return (
    <DialogContent includeCloseButton>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (newEmail.trim() && newEmail !== currentEmail)
            mutation.mutate(newEmail.trim());
        }}
      >
        <DialogHeader>
          <DialogTitle>Change Email</DialogTitle>
          <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
            Update the email address for this user
          </p>
        </DialogHeader>
        <div className='py-4'>
          <Input
            type='email'
            placeholder='New email'
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant='outline' type='button' onClick={onClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            disabled={
              mutation.isPending ||
              !newEmail.trim() ||
              newEmail === currentEmail
            }
          >
            {mutation.isPending ? 'Saving...' : 'Update Email'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function DeleteUserDialog({
  userId,
  email,
  onClose,
}: {
  userId: string;
  email: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Hard delete is irreversible (better-auth `removeUser` drops the user, their
  // sessions, and accounts), so require typing the exact email to confirm.
  const [confirmEmail, setConfirmEmail] = useState('');
  const confirmed = confirmEmail.trim() === email;

  const mutation = useMutation({
    mutationFn: async () => {
      const result = await authClient.admin.removeUser({ userId });
      return unwrapBaResponse(result);
    },
    onSuccess: () => {
      toast.success('User deleted');
      void queryClient.invalidateQueries({ queryKey: adminUsersQueryKey });
      onClose();
      void navigate({ to: '/app/admin' });
    },
    onError: (error) => {
      moduleLogger.error({ err: error }, 'Failed to delete user');
      toast.error(
        error instanceof BaError ? error.message : 'Failed to delete user',
      );
    },
  });

  return (
    <DialogContent includeCloseButton>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (confirmed) mutation.mutate();
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
            This permanently deletes <strong>{email}</strong> along with their
            sessions and accounts. This cannot be undone. Type the email to
            confirm.
          </p>
        </DialogHeader>
        <div className='py-4'>
          <Input
            type='email'
            placeholder={email}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant='outline' type='button' onClick={onClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='destructive'
            disabled={mutation.isPending || !confirmed}
          >
            {mutation.isPending ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
